import { useEffect } from "react";

const DEFAULT_META = {
  title: "Energea Brasil | Economia de energia para empresas",
  description:
    "Reduza o custo de energia da sua empresa com a Energea Brasil. Simule economia e cadastre-se em minutos.",
  canonical: "https://www.energea.com.br/",
  robots:
    "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
  ogLocale: "pt_BR",
  ogSiteName: "Energea Brasil",
  twitterCard: "summary_large_image",
};

const setMetaTag = (selector, attributes) => {
  if (typeof document === "undefined") return;

  let node = document.head.querySelector(selector);
  if (!node) {
    node = document.createElement("meta");
    document.head.appendChild(node);
  }

  Object.keys(attributes).forEach((key) => {
    node.setAttribute(key, attributes[key]);
  });
};

const setCanonical = (href) => {
  if (typeof document === "undefined") return;

  let node = document.head.querySelector('link[rel="canonical"]');
  if (!node) {
    node = document.createElement("link");
    node.setAttribute("rel", "canonical");
    document.head.appendChild(node);
  }
  node.setAttribute("href", href);
};

const removeManagedJsonLd = () => {
  if (typeof document === "undefined") return;
  const scripts = document.head.querySelectorAll(
    'script[type="application/ld+json"][data-managed-seo="true"]',
  );
  scripts.forEach((script) => script.remove());
};

const injectJsonLd = (jsonLd = []) => {
  if (typeof document === "undefined" || !Array.isArray(jsonLd)) return;
  jsonLd.forEach((entry) => {
    const script = document.createElement("script");
    script.setAttribute("type", "application/ld+json");
    script.setAttribute("data-managed-seo", "true");
    script.text = JSON.stringify(entry);
    document.head.appendChild(script);
  });
};

const applyMeta = ({
  title,
  description,
  canonical,
  robots,
  ogTitle,
  ogDescription,
  ogImageHeight,
  ogUrl,
  ogImageWidth,
  ogLocale,
  ogSiteName,
  ogType,
  ogImage,
  twitterCard,
  twitterTitle,
  twitterDescription,
  twitterImage,
}) => {
  if (typeof document === "undefined") return;

  document.title = title;
  setCanonical(canonical);
  setMetaTag('meta[name="description"]', { name: "description", content: description });
  setMetaTag('meta[name="robots"]', { name: "robots", content: robots });

  setMetaTag('meta[property="og:title"]', { property: "og:title", content: ogTitle });
  setMetaTag('meta[property="og:description"]', {
    property: "og:description",
    content: ogDescription,
  });
  setMetaTag('meta[property="og:url"]', { property: "og:url", content: ogUrl });
  setMetaTag('meta[property="og:type"]', { property: "og:type", content: ogType });
  setMetaTag('meta[property="og:site_name"]', {
    property: "og:site_name",
    content: ogSiteName,
  });
  setMetaTag('meta[property="og:locale"]', {
    property: "og:locale",
    content: ogLocale,
  });
  if (ogImage) {
    setMetaTag('meta[property="og:image"]', { property: "og:image", content: ogImage });
  }
  if (ogImageWidth) {
    setMetaTag('meta[property="og:image:width"]', {
      property: "og:image:width",
      content: ogImageWidth,
    });
  }
  if (ogImageHeight) {
    setMetaTag('meta[property="og:image:height"]', {
      property: "og:image:height",
      content: ogImageHeight,
    });
  }

  setMetaTag('meta[property="twitter:title"]', {
    property: "twitter:title",
    content: twitterTitle,
  });
  setMetaTag('meta[name="twitter:card"]', {
    name: "twitter:card",
    content: twitterCard,
  });
  setMetaTag('meta[property="twitter:description"]', {
    property: "twitter:description",
    content: twitterDescription,
  });
  if (twitterImage) {
    setMetaTag('meta[property="twitter:image"]', {
      property: "twitter:image",
      content: twitterImage,
    });
  }
};

const buildMeta = (meta) => {
  const title = meta.title || DEFAULT_META.title;
  const description = meta.description || DEFAULT_META.description;
  const canonical = meta.canonical || DEFAULT_META.canonical;

  return {
    title,
    description,
  canonical,
  robots: meta.robots || DEFAULT_META.robots,
  ogTitle: meta.ogTitle || title,
  ogDescription: meta.ogDescription || description,
  ogUrl: meta.ogUrl || canonical,
  ogType: meta.ogType || "website",
  ogLocale: meta.ogLocale || DEFAULT_META.ogLocale,
  ogSiteName: meta.ogSiteName || DEFAULT_META.ogSiteName,
  ogImage: meta.ogImage || null,
  ogImageWidth: meta.ogImageWidth || null,
  ogImageHeight: meta.ogImageHeight || null,
  twitterCard: meta.twitterCard || DEFAULT_META.twitterCard,
  twitterTitle: meta.twitterTitle || title,
  twitterDescription: meta.twitterDescription || description,
  twitterImage: meta.twitterImage || meta.ogImage || null,
  };
};

const useSeoMeta = (meta = {}) => {
  const jsonLdString = JSON.stringify(meta.jsonLd || []);
  const canonical = meta.canonical;
  const description = meta.description;
  const ogDescription = meta.ogDescription;
  const ogImage = meta.ogImage;
  const ogImageHeight = meta.ogImageHeight;
  const ogImageWidth = meta.ogImageWidth;
  const ogLocale = meta.ogLocale;
  const ogSiteName = meta.ogSiteName;
  const ogTitle = meta.ogTitle;
  const ogType = meta.ogType;
  const ogUrl = meta.ogUrl;
  const robots = meta.robots;
  const title = meta.title;
  const twitterCard = meta.twitterCard;
  const twitterDescription = meta.twitterDescription;
  const twitterImage = meta.twitterImage;
  const twitterTitle = meta.twitterTitle;

  useEffect(() => {
    const resolvedMeta = buildMeta({
      canonical,
      description,
      ogDescription,
      ogImage,
      ogImageHeight,
      ogImageWidth,
      ogLocale,
      ogSiteName,
      ogTitle,
      ogType,
      ogUrl,
      robots,
      title,
      twitterCard,
      twitterDescription,
      twitterImage,
      twitterTitle,
    });
    applyMeta(resolvedMeta);

    removeManagedJsonLd();
    injectJsonLd(JSON.parse(jsonLdString));

    return () => {
      applyMeta(buildMeta(DEFAULT_META));
      removeManagedJsonLd();
    };
  }, [
    canonical,
    description,
    jsonLdString,
    ogDescription,
    ogImage,
    ogImageHeight,
    ogImageWidth,
    ogLocale,
    ogSiteName,
    ogTitle,
    ogType,
    ogUrl,
    robots,
    title,
    twitterCard,
    twitterDescription,
    twitterImage,
    twitterTitle,
  ]);
};

export default useSeoMeta;
